import React, { useState, useEffect } from 'react';
import { 
  Car, 
  TrendingUp, 
  TrendingDown, 
  Star, 
  Wrench, 
  MapPin, 
  Calendar,
  DollarSign,
  Gauge,
  Filter,
  Search,
  BarChart3,
  AlertTriangle,
  CheckCircle,
  Clock,
  Fuel,
  Users,
  ChevronDown,
  ChevronUp,
  Plus,
  Minus
} from 'lucide-react';

const SixtVehiclePerformance = () => {
  const [expandedCategories, setExpandedCategories] = useState(new Set(['bmw', 'toyota'])); // Start with some expanded

  // Generate pivot table data structure
  const generatePivotData = () => {
    const categories = [
      { 
        key: 'bmw', 
        name: 'BMW',
        locations: [
          { key: 'istanbul', name: 'Istanbul Atatürk', vehicleCount: 25 },
          { key: 'ankara', name: 'Ankara Esenboğa', vehicleCount: 18 },
          { key: 'antalya', name: 'Antalya Airport', vehicleCount: 22 }
        ]
      },
      { 
        key: 'mercedes', 
        name: 'Mercedes-Benz',
        locations: [
          { key: 'istanbul', name: 'Istanbul Atatürk', vehicleCount: 20 },
          { key: 'ankara', name: 'Ankara Esenboğa', vehicleCount: 12 },
          { key: 'antalya', name: 'Antalya Airport', vehicleCount: 13 }
        ]
      },
      { 
        key: 'toyota', 
        name: 'Toyota',
        locations: [
          { key: 'istanbul', name: 'Istanbul Atatürk', vehicleCount: 45 },
          { key: 'ankara', name: 'Ankara Esenboğa', vehicleCount: 30 },
          { key: 'izmir', name: 'İzmir Adnan Menderes', vehicleCount: 35 },
          { key: 'antalya', name: 'Antalya Airport', vehicleCount: 40 }
        ]
      },
      { 
        key: 'ford', 
        name: 'Ford',
        locations: [
          { key: 'istanbul', name: 'Istanbul Atatürk', vehicleCount: 35 },
          { key: 'ankara', name: 'Ankara Esenboğa', vehicleCount: 25 },
          { key: 'izmir', name: 'İzmir Adnan Menderes', vehicleCount: 25 },
          { key: 'antalya', name: 'Antalya Airport', vehicleCount: 20 }
        ]
      },
      { 
        key: 'volkswagen', 
        name: 'Volkswagen',
        locations: [
          { key: 'istanbul', name: 'Istanbul Atatürk', vehicleCount: 30 },
          { key: 'ankara', name: 'Ankara Esenboğa', vehicleCount: 20 },
          { key: 'izmir', name: 'İzmir Adnan Menderes', vehicleCount: 15 },
          { key: 'bursa', name: 'Bursa City Center', vehicleCount: 20 }
        ]
      },
      { 
        key: 'renault', 
        name: 'Renault',
        locations: [
          { key: 'istanbul', name: 'Istanbul Atatürk', vehicleCount: 25 },
          { key: 'ankara', name: 'Ankara Esenboğa', vehicleCount: 15 },
          { key: 'izmir', name: 'İzmir Adnan Menderes', vehicleCount: 25 },
          { key: 'bursa', name: 'Bursa City Center', vehicleCount: 20 },
          { key: 'trabzon', name: 'Trabzon Airport', vehicleCount: 40 }
        ]
      }
    ];

    const months = [
      { key: 'june', name: 'Haziran', short: 'Haz' },
      { key: 'july', name: 'Temmuz', short: 'Tem' },
      { key: 'august', name: 'Ağustos', short: 'Ağu' },
      { key: 'september', name: 'Eylül', short: 'Eyl' },
      { key: 'october', name: 'Ekim', short: 'Eki' },
      { key: 'november', name: 'Kasım', short: 'Kas' }
    ];

    // Generate revenue data for each category > location > month
    const pivotData = categories.map(category => {
      const categoryData = {
        ...category,
        monthlyTotals: {},
        totalRevenue: 0,
        locations: category.locations.map(location => {
          const locationMonthlyData = months.map((month, monthIndex) => {
            // Different base rates for different car brands
            let baseDailyRate;
            switch (category.key) {
              case 'bmw':
                baseDailyRate = 750 + (Math.random() * 150 - 75);
                break;
              case 'mercedes':
                baseDailyRate = 800 + (Math.random() * 200 - 100);
                break;
              case 'toyota':
                baseDailyRate = 280 + (Math.random() * 60 - 30);
                break;
              case 'ford':
                baseDailyRate = 320 + (Math.random() * 80 - 40);
                break;
              case 'volkswagen':
                baseDailyRate = 380 + (Math.random() * 70 - 35);
                break;
              case 'renault':
                baseDailyRate = 260 + (Math.random() * 50 - 25);
                break;
              default:
                baseDailyRate = 350;
            }

            // Seasonal factors
            let seasonalFactor = 1;
            if (location.key === 'antalya') {
              // Tourism seasonality
              seasonalFactor = monthIndex < 2 ? 1.4 : monthIndex > 3 ? 0.7 : 1.1;
            } else if (location.key === 'istanbul') {
              seasonalFactor = 1.1 + (Math.random() * 0.2 - 0.1);
            }

            // Brand-specific utilization
            let baseUtilization;
            switch (category.key) {
              case 'bmw':
                baseUtilization = 72; // Premium brand, lower utilization
                break;
              case 'mercedes':
                baseUtilization = 70; // Premium brand, lower utilization
                break;
              case 'toyota':
                baseUtilization = 89; // Popular, high utilization
                break;
              case 'ford':
                baseUtilization = 85; // Good utilization
                break;
              case 'volkswagen':
                baseUtilization = 83; // Solid utilization
                break;
              case 'renault':
                baseUtilization = 87; // Good economy utilization
                break;
              default:
                baseUtilization = 80;
            }

            const utilization = Math.max(60, Math.min(95, baseUtilization * seasonalFactor + (Math.random() * 10 - 5)));
            const dailyRate = Math.round(baseDailyRate * seasonalFactor);
            const daysInMonth = 30;
            const utilizedDays = Math.round((utilization / 100) * daysInMonth);
            const monthlyRevenue = Math.round(location.vehicleCount * utilizedDays * dailyRate);

            return {
              month: month.key,
              monthName: month.name,
              monthShort: month.short,
              revenue: monthlyRevenue,
              utilization: Math.round(utilization * 10) / 10,
              dailyRate: dailyRate,
              utilizedDays: utilizedDays
            };
          });

          const locationTotalRevenue = locationMonthlyData.reduce((sum, m) => sum + m.revenue, 0);

          return {
            ...location,
            monthlyData: locationMonthlyData,
            totalRevenue: locationTotalRevenue
          };
        })
      };

      // Calculate category totals by month
      months.forEach(month => {
        const monthTotal = categoryData.locations.reduce((sum, location) => {
          const monthData = location.monthlyData.find(m => m.month === month.key);
          return sum + (monthData ? monthData.revenue : 0);
        }, 0);
        categoryData.monthlyTotals[month.key] = monthTotal;
      });

      // Calculate category total revenue
      categoryData.totalRevenue = categoryData.locations.reduce((sum, location) => sum + location.totalRevenue, 0);

      return categoryData;
    });

    return { categories: pivotData, months };
  };

  const { categories, months } = generatePivotData();

  // Toggle category expansion
  const toggleCategoryExpansion = (categoryKey) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryKey)) {
      newExpanded.delete(categoryKey);
    } else {
      newExpanded.add(categoryKey);
    }
    setExpandedCategories(newExpanded);
  };

  // Format revenue value
  const formatRevenue = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toLocaleString();
  };

  // Calculate grand totals
  const grandTotals = {
    byMonth: {},
    total: 0
  };

  months.forEach(month => {
    grandTotals.byMonth[month.key] = categories.reduce((sum, category) => 
      sum + (category.monthlyTotals[month.key] || 0), 0
    );
  });

  grandTotals.total = categories.reduce((sum, category) => sum + category.totalRevenue, 0);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Car className="h-8 w-8 text-blue-600 mr-3" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Araç Marka Performans Analizi</h1>
            <p className="text-gray-600">Marka ve lokasyon bazında aylık gelir analizi (₺)</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Toplam 6 Aylık Gelir</p>
              <p className="text-2xl font-bold text-purple-600">{(grandTotals.total / 1000000).toFixed(1)}M ₺</p>
            </div>
            <DollarSign className="h-8 w-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Aylık Ortalama</p>
              <p className="text-2xl font-bold text-blue-600">{(grandTotals.total / 6000000).toFixed(1)}M ₺</p>
            </div>
            <Calendar className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">En Yüksek Marka</p>
              <p className="text-2xl font-bold text-green-600">
                {categories.sort((a, b) => b.totalRevenue - a.totalRevenue)[0]?.name?.split(' ')[0] || 'N/A'}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Toplam Marka</p>
              <p className="text-2xl font-bold text-gray-600">{categories.length}</p>
            </div>
            <BarChart3 className="h-8 w-8 text-gray-600" />
          </div>
        </div>
      </div>

      {/* Pivot Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-900 border-b border-gray-200 sticky left-0 bg-gray-50 z-10 min-w-[280px]">
                  Marka / Lokasyon
                </th>
                {months.map(month => (
                  <th key={month.key} className="px-4 py-4 text-center text-sm font-bold text-gray-900 border-b border-gray-200 min-w-[120px]">
                    {month.short} 2024
                  </th>
                ))}
                <th className="px-6 py-4 text-center text-sm font-bold text-gray-900 border-b border-gray-200 min-w-[140px]">
                  Toplam (6 Ay)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {categories.map((category, categoryIndex) => (
                <React.Fragment key={category.key}>
                  {/* Category Header Row */}
                  <tr className="bg-blue-50 hover:bg-blue-100 border-b border-gray-200">
                    <td className="px-6 py-4 sticky left-0 bg-blue-50 hover:bg-blue-100 z-10">
                      <div className="flex items-center">
                        <button
                          onClick={() => toggleCategoryExpansion(category.key)}
                          className="mr-3 p-1 hover:bg-blue-200 rounded"
                        >
                          {expandedCategories.has(category.key) ? 
                            <Minus className="h-4 w-4 text-blue-700" /> : 
                            <Plus className="h-4 w-4 text-blue-700" />
                          }
                        </button>
                        <Car className="h-5 w-5 text-blue-700 mr-2" />
                        <span className="font-bold text-blue-900">{category.name}</span>
                      </div>
                    </td>
                    
                    {months.map(month => (
                      <td key={month.key} className="px-4 py-4 text-center border-b border-gray-200">
                        <span className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-bold bg-blue-100 text-blue-800">
                          {formatRevenue(category.monthlyTotals[month.key])} ₺
                        </span>
                      </td>
                    ))}
                    
                    <td className="px-6 py-4 text-center border-b border-gray-200">
                      <span className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-bold bg-blue-200 text-blue-900">
                        {formatRevenue(category.totalRevenue)} ₺
                      </span>
                    </td>
                  </tr>

                  {/* Location Rows (when expanded) */}
                  {expandedCategories.has(category.key) && category.locations.map((location, locationIndex) => (
                    <tr key={`${category.key}-${location.key}`} className="hover:bg-gray-50 border-b border-gray-100">
                      <td className="px-6 py-3 sticky left-0 bg-white hover:bg-gray-50 z-10">
                        <div className="flex items-center ml-10">
                          <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{location.name}</div>
                            <div className="text-xs text-gray-500">{location.vehicleCount} araç</div>
                          </div>
                        </div>
                      </td>
                      
                      {location.monthlyData.map(monthData => (
                        <td key={monthData.month} className="px-4 py-3 text-center border-b border-gray-100">
                          <div className="text-sm font-medium text-gray-900">
                            {formatRevenue(monthData.revenue)} ₺
                          </div>
                          <div className="text-xs text-gray-500">
                            {monthData.utilization}% kullanım
                          </div>
                        </td>
                      ))}
                      
                      <td className="px-6 py-3 text-center border-b border-gray-100">
                        <div className="text-sm font-medium text-gray-900">
                          {formatRevenue(location.totalRevenue)} ₺
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}

              {/* Grand Total Row */}
              <tr className="bg-green-50 border-t-2 border-green-200">
                <td className="px-6 py-4 sticky left-0 bg-green-50 z-10">
                  <div className="flex items-center">
                    <DollarSign className="h-5 w-5 text-green-700 mr-2" />
                    <span className="font-bold text-green-900">GENEL TOPLAM</span>
                  </div>
                </td>
                
                {months.map(month => (
                  <td key={month.key} className="px-4 py-4 text-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-bold bg-green-200 text-green-800">
                      {formatRevenue(grandTotals.byMonth[month.key])} ₺
                    </span>
                  </td>
                ))}
                
                <td className="px-6 py-4 text-center">
                  <span className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-bold bg-green-300 text-green-900">
                    {formatRevenue(grandTotals.total)} ₺
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Category Performance Analysis */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 text-green-500 mr-2" />
            En Yüksek Gelir (Marka)
          </h3>
          <div className="space-y-3">
            {categories
              .sort((a, b) => b.totalRevenue - a.totalRevenue)
              .map((category, index) => (
                <div key={category.key} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white mr-3 ${
                      index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-orange-500'
                    }`}>
                      {index + 1}
                    </div>
                    <span className="text-sm font-medium">{category.name}</span>
                  </div>
                  <span className="text-sm text-gray-600">{formatRevenue(category.totalRevenue)} ₺</span>
                </div>
              ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <MapPin className="h-5 w-5 text-blue-500 mr-2" />
            En Yüksek Gelir (Lokasyon)
          </h3>
          <div className="space-y-3">
            {categories
              .flatMap(category => 
                category.locations.map(location => ({
                  ...location,
                  categoryName: category.name
                }))
              )
              .sort((a, b) => b.totalRevenue - a.totalRevenue)
              .slice(0, 5)
              .map((location, index) => (
                <div key={`${location.categoryName}-${location.key}`} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white mr-3 ${
                      index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-orange-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <span className="text-sm font-medium">{location.name}</span>
                      <div className="text-xs text-gray-500">{location.categoryName}</div>
                    </div>
                  </div>
                  <span className="text-sm text-gray-600">{formatRevenue(location.totalRevenue)} ₺</span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start">
          <BarChart3 className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-1">Pivot Table Kullanımı</h4>
            <p className="text-sm text-blue-800">
              • <strong>Markalar:</strong> Ana grupları görmek için <Plus className="h-3 w-3 inline" />/<Minus className="h-3 w-3 inline" /> butonlarını kullanın<br/>
              • <strong>Rakamlar:</strong> Tüm değerler aylık gelir (₺) cinsinden<br/>
              • <strong>K:</strong> Binler (örn: 150K = 150.000 ₺)<br/>
              • <strong>M:</strong> Milyonlar (örn: 2.5M = 2.500.000 ₺)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SixtVehiclePerformance; 